import type { Component } from 'vue';
import { defineAsyncComponent, markRaw } from 'vue';
import type { Router, RouteLocationRaw } from 'vue-router';
import { gql } from '@apollo/client/core';
import { getActivePinia } from 'pinia';

import { apolloClient } from '@/apolloClient';
import { GET_FORM_DETAILS } from '@/graphql/Forms';
import { useUserStore } from '@/store/userStore';

/**
 * Host to build a school's public links on: its own public URL (custom domain,
 * tenant subdomain, or the platform URL — `School.frontend_url`), falling back
 * to the current origin when no school is active. Reads the store only when a
 * Pinia instance is live, so the resolver never throws outside the app shell.
 */
function schoolPublicOrigin(): string {
  const pinia = getActivePinia();
  const school = pinia ? useUserStore(pinia).currentSchool : null;
  return school?.frontend_url || window.location.origin;
}
import { readIdProp } from './aiCardRegistry';

/**
 * Registry mapping an action `key` declared by the backend onto a frontend
 * handler.
 *
 * Division of responsibility (by design):
 *  - The BACKEND declares WHAT the action is (`key`, `label`, `icon`) and has
 *    ALREADY filtered the list to what this user is permitted to do. It
 *    deliberately sends no routes and no modes.
 *  - The FRONTEND owns HOW, including the CONTAINMENT MODE.
 *
 * Containment (Paul): "A drawer where possible, a modal where possible, and an
 * embed destinations in the workspace where possible." Preference order is
 * drawer > modal > embed > navigate, chosen per action by what genuinely
 * exists — never by forcing a screen into a container it wasn't built for.
 * Navigation is a deliberate last resort for destinations that are their own
 * workspace (form builder, CBT authoring/monitoring, full report screens).
 *
 * These buttons are USER-triggered only. The AI never invokes them; it acts
 * solely through MCP tools.
 *
 * Unknown keys are skipped silently. The two sides evolve separately, so a key
 * this frontend doesn't recognise must render nothing at all — never a dead
 * button, never a user-visible warning.
 */

/** An action as declared by the backend on a component payload. */
export interface AiCardAction {
  key: string;
  label: string;
  icon?: string | null;
}

export type AiActionMode = 'drawer' | 'modal' | 'embed' | 'navigate';

/** A card to render inside the workspace (mirrors AiWorkspaceCardData). */
export interface AiActionEmbedCard {
  id: string;
  type: string;
  props: Record<string, unknown>;
  title?: string | null;
  actions?: Array<{ key: string; label: string; icon?: string | null }>;
}

/**
 * What the host should do. The card itself performs nothing — it hands this
 * back so the page can keep the conversation and workspace intact.
 */
export type AiActionOutcome =
  | { mode: 'navigate'; route: RouteLocationRaw }
  | {
    mode: 'drawer' | 'modal';
    component: Component;
    props: Record<string, unknown>;
    title: string;
  }
  | { mode: 'embed'; card: AiActionEmbedCard };

export interface AiCardActionContext {
  /** The card's props (the ids the model sent). */
  props: Record<string, unknown>;
  /** The card's title, useful as a drawer/embed heading. */
  title?: string | null;
  router: Router;
}

export interface AiCardActionDefinition {
  /** How this action is contained. Declared so it can be asserted in tests. */
  mode: AiActionMode;
  /**
   * Whether the action can actually be carried out right now — e.g. the card
   * carries the id the handler needs and the target route exists in this app
   * build. Anything unavailable is skipped exactly like an unknown key.
   */
  isAvailable: (ctx: AiCardActionContext) => boolean;
  /**
   * Produce the outcome. May be async (resolving a slug or a student record
   * first). Returning null aborts silently rather than doing something wrong.
   */
  resolve: (ctx: AiCardActionContext) => AiActionOutcome | null | Promise<AiActionOutcome | null>;
}

const studentIdFrom = (props: Record<string, unknown>) =>
  readIdProp(props, 'studentId', 'student_id', 'id');

const formIdFrom = (props: Record<string, unknown>) =>
  readIdProp(props, 'formId', 'form_id', 'id');

// ---------------------------------------------------------------------------
// Helpers per mode
// ---------------------------------------------------------------------------

/** navigate: a named route needing the student id as `id`. */
function studentRouteAction(routeName: string): AiCardActionDefinition {
  return {
    mode: 'navigate',
    isAvailable: ({ props, router }) => !!studentIdFrom(props) && router.hasRoute(routeName),
    resolve: ({ props }) => {
      const id = studentIdFrom(props);
      return id ? { mode: 'navigate', route: { name: routeName, params: { id } } } : null;
    },
  };
}

/** navigate: a named route needing no ids at all. */
function plainRouteAction(
  routeName: string,
  query?: Record<string, string>,
): AiCardActionDefinition {
  return {
    mode: 'navigate',
    isAvailable: ({ router }) => router.hasRoute(routeName),
    resolve: () => ({
      mode: 'navigate',
      route: { name: routeName, ...(query ? { query } : {}) },
    }),
  };
}

/** navigate: a named route keyed on some other id, passed as `id`. */
function idRouteAction(routeName: string, propNames: string[]): AiCardActionDefinition {
  const read = (props: Record<string, unknown>) => readIdProp(props, ...propNames);
  return {
    mode: 'navigate',
    isAvailable: ({ props, router }) => !!read(props) && router.hasRoute(routeName),
    resolve: ({ props }) => {
      const id = read(props);
      return id ? { mode: 'navigate', route: { name: routeName, params: { id } } } : null;
    },
  };
}

/**
 * embed: add a card the workspace already knows how to render, beside the chat
 * rather than over it.
 */
function embedCardAction(
  type: string,
  buildProps: (props: Record<string, unknown>) => Record<string, unknown> | null,
  fallbackTitle: string,
): AiCardActionDefinition {
  return {
    mode: 'embed',
    isAvailable: ({ props }) => buildProps(props) !== null,
    resolve: ({ props, title }) => {
      const cardProps = buildProps(props);
      if (!cardProps) return null;
      return {
        mode: 'embed',
        card: {
          // Stable id so re-running the action re-surfaces the same card
          // instead of stacking duplicates.
          id: `embed:${type}:${Object.values(cardProps).join(':')}`,
          type,
          props: cardProps,
          title: title ? `${fallbackTitle} — ${title}` : fallbackTitle,
          actions: [],
        },
      };
    },
  };
}

/** Minimal student lookup so a drawer can be opened pre-filled from an id. */
const STUDENT_FOR_ACTION = gql`
  query AiActionStudentBasic($id: ID!) {
    student(id: $id) {
      id
      first_name
      surname
      matric_no
    }
  }
`;

export const AI_CARD_ACTION_REGISTRY: Record<string, AiCardActionDefinition> = {
  // --- student_card -------------------------------------------------------

  // NAVIGATE: the student profile is a full page of its own (personal info,
  // enrolment history, subjects, attendance, bills). No drawer exists for it —
  // StudentProfileDrawer is the parent-portal child view, keyed by schoolId —
  // and cramming the whole page into one would half-work.
  open_student_profile: studentRouteAction('StudentOverview'),

  // EMBED: the workspace already renders billing_summary, so this lands beside
  // the chat instead of navigating away.
  open_student_billing: embedCardAction(
    'billing_summary',
    (props) => {
      const studentId = studentIdFrom(props);
      return studentId ? { studentId } : null;
    },
    'Billing summary',
  ),

  // EMBED: same reasoning — results_summary is an existing renderer.
  open_student_results: embedCardAction(
    'results_summary',
    (props) => {
      const studentId = studentIdFrom(props);
      return studentId ? { studentId } : null;
    },
    'Results',
  ),

  // --- billing_summary ----------------------------------------------------

  // DRAWER: RecordPaymentDrawer is already a SlideOverDrawer and takes an
  // optional pre-selected student, so it opens over the assistant page with
  // the student pre-filled. The student record is resolved from the id first;
  // on the school-wide billing cards (no student id) the drawer opens with
  // its own student picker instead.
  record_payment: {
    mode: 'drawer',
    isAvailable: () => true,
    resolve: async({ props }) => {
      const id = studentIdFrom(props);
      let student: Record<string, unknown> | null = null;
      if (id) {
        try {
          const { data } = await apolloClient.query<{
            student?: {
              id: string; first_name: string; surname?: string | null; matric_no?: string | null;
            } | null;
          }>({ query: STUDENT_FOR_ACTION, variables: { id }, fetchPolicy: 'cache-first' });
          if (data?.student) {
            student = {
              id: data.student.id,
              first_name: data.student.first_name,
              surname: data.student.surname ?? '',
              ...(data.student.matric_no ? { matric_no: data.student.matric_no } : {}),
            };
          }
        } catch {
          // Fall through: the drawer still opens, with its own student picker.
        }
      }
      return {
        mode: 'drawer',
        // markRaw: a component definition must never be made deeply reactive
        // (Vue warns, and it costs needless proxying).
        component: markRaw(defineAsyncComponent(
          () => import('@/components/DashBoard/Billing/RecordPaymentDrawer.vue'),
        )),
        props: { student },
        title: 'Record Payment',
      };
    },
  },

  // --- results_summary ----------------------------------------------------

  // NAVIGATE: BroadsheetReport declares no props and owns its own filters and
  // export controls — a class-wide report screen, not an embeddable widget.
  view_class_broadsheet: plainRouteAction('admin-broadsheet-reports'),

  // NAVIGATE: score entry is a full editing surface. Only renders when the
  // card supplies both ids — a student-only card can't reach a scores screen.
  upload_scores: {
    mode: 'navigate',
    isAvailable: ({ props, router }) =>
      !!readIdProp(props, 'subjectId', 'subject_id')
      && !!readIdProp(props, 'sectionId', 'section_id', 'classroomSectionId')
      && router.hasRoute('admin-subject-scores'),
    resolve: ({ props }) => {
      const subjectId = readIdProp(props, 'subjectId', 'subject_id');
      const sectionId = readIdProp(props, 'sectionId', 'section_id', 'classroomSectionId');
      if (!subjectId || !sectionId) return null;
      return {
        mode: 'navigate',
        route: { name: 'admin-subject-scores', params: { subjectId, sectionId } },
      };
    },
  },

  // --- student_course_registration --------------------------------------------------------

  // NAVIGATE: ViewCourseRegistration reads route.params.studentId directly, so
  // it cannot be embedded without rebuilding it.
  open_course_registration: {
    mode: 'navigate',
    isAvailable: ({ props, router }) =>
      !!studentIdFrom(props) && router.hasRoute('admin-course-registration-view'),
    resolve: ({ props }) => {
      const studentId = studentIdFrom(props);
      return studentId
        ? {
          mode: 'navigate',
          route: { name: 'admin-course-registration-view', params: { studentId } },
        }
        : null;
    },
  },

  // NAVIGATE: a review queue with its own tabs and bulk actions.
  review_course_registrations: plainRouteAction('admin-course', { tab: 'approvals' }),

  // --- student_subject_enrollment (declared for later) --------------------

  view_subject_offerings: plainRouteAction('SubjectOfferings'),

  // --- student_attendance / class_attendance ------------------------------

  // NAVIGATE: marking happens on the daily attendance screen; the cards
  // themselves are read-only summaries.
  open_daily_attendance: plainRouteAction('admin-daily-attendance'),

  // --- staff_card ----------------------------------------------------------

  // NAVIGATE: the teacher-details page adds role management and actions the
  // read-only card deliberately leaves out. Keyed on the school_staff id.
  open_staff_profile: idRouteAction('teacher-details', ['staffId', 'staff_id', 'id']),

  // --- debtors_list --------------------------------------------------------

  // NAVIGATE: the full debtors screen adds filters, sorting and export.
  open_debtors: plainRouteAction('ADMIN_BILLING_DEBTORS'),

  // --- curriculum ----------------------------------------------------------

  open_scheme_of_work: {
    mode: 'navigate',
    isAvailable: ({ props, router }) =>
      (readIdProp(props, 'schemeOfWorkId', 'scheme_of_work_id')
        ? router.hasRoute('SchemeOfWorkDetails')
        : router.hasRoute('SchemesOfWork')),
    resolve: ({ props }) => {
      const id = readIdProp(props, 'schemeOfWorkId', 'scheme_of_work_id');
      return id
        ? { mode: 'navigate', route: { name: 'SchemeOfWorkDetails', params: { id } } }
        : { mode: 'navigate', route: { name: 'SchemesOfWork' } };
    },
  },

  view_lesson_notes: plainRouteAction('LessonNotes'),

  // --- cbt_tests / cbt_results (declared for later) -----------------------
  // All NAVIGATE: CBT authoring and live monitoring are their own workspaces.

  open_cbt_tests: plainRouteAction('admin-cbt'),
  create_cbt_test: plainRouteAction('admin-cbt-create'),
  monitor_cbt_sessions: plainRouteAction('admin-cbt-monitoring'),

  // --- timetable (declared for later) -------------------------------------
  // One screen handles both viewing and editing; it owns a full grid editor.

  open_timetable: plainRouteAction('admin-timetable'),
  edit_timetable: plainRouteAction('admin-timetable'),

  // --- form_preview / form_edit --------------------------------------------

  /**
   * EMBED: the published form renders in the workspace via its public URL, so
   * the user can read it beside the conversation. The public route is keyed by
   * SLUG while cards carry a form id, so the slug is resolved first; if it
   * can't be resolved (deleted form, unpublished, request failed) the action
   * quietly does nothing rather than showing a broken frame.
   */
  open_form_preview: {
    mode: 'embed',
    isAvailable: ({ props }) => !!formIdFrom(props),
    resolve: async({ props, title }) => {
      const id = formIdFrom(props);
      if (!id) return null;
      try {
        const { data } = await apolloClient.query<{
          form?: { slug?: string | null; public_url?: string | null; title?: string | null } | null;
        }>({ query: GET_FORM_DETAILS, variables: { id }, fetchPolicy: 'cache-first' });
        const form = data?.form;
        // Same host rule as the Forms list: the school's public URL, not the
        // origin the admin happens to be browsing from.
        const url = form?.public_url
          || (form?.slug ? `${schoolPublicOrigin()}/forms/${form.slug}` : null);
        if (!url) return null;
        return {
          mode: 'embed',
          card: {
            id: `embed:form_preview:${id}`,
            type: 'form_preview',
            props: { url, formId: id },
            title: form?.title || title || 'Form preview',
            actions: [],
          },
        };
      } catch {
        return null;
      }
    },
  },

  // EMBED: the builder itself renders in the workspace (FormBuilderView in
  // embedded mode via the form_edit card), so the form can be worked on
  // directly beside the conversation instead of navigating away.
  edit_form: embedCardAction(
    'form_edit',
    (props) => {
      const formId = formIdFrom(props);
      return formId ? { formId } : null;
    },
    'Form builder',
  ),

  // NAVIGATE: submissions is a filterable table with its own review flow.
  view_form_submissions: idRouteAction('admin-forms-submissions', ['formId', 'form_id', 'id']),

  // --- billing workspace cards --------------------------------------------

  // DRAWER: CreateBillDrawer is the app's own 4-step wizard (target → items →
  // settings → review, duplicate preflight included) and already a
  // SlideOverDrawer, so bills are created beside the conversation with the
  // exact flow the Bills screen uses.
  create_bill: {
    mode: 'drawer',
    isAvailable: () => true,
    resolve: () => ({
      mode: 'drawer',
      component: markRaw(defineAsyncComponent(
        () => import('@/components/DashBoard/Billing/CreateBillDrawer.vue'),
      )),
      props: {},
      title: 'Create Bill',
    }),
  },

  // MODAL: the human gate Paul specified for actions the assistant must never
  // perform — a summary of exactly what publishing does (who, items, totals,
  // reach) above the one button that does it, permission-checked at every
  // layer (action filtering server-side, can() in the modal, the mutation's
  // ability). The assistant only brings the user here.
  publish_bill: {
    mode: 'modal',
    isAvailable: ({ props }) => !!readIdProp(props, 'billId', 'bill_id', 'id'),
    resolve: ({ props }) => {
      const billId = readIdProp(props, 'billId', 'bill_id', 'id');
      if (!billId) return null;
      return {
        mode: 'modal',
        component: markRaw(defineAsyncComponent(() => import('./AiPublishBillModal.vue'))),
        props: { billId },
        title: 'Publish bill',
      };
    },
  },

  // NAVIGATE: full screens that are their own workspaces — dashboards,
  // filterable tables, report builders and the fee items manager.
  open_billing_dashboard: plainRouteAction('ADMIN_BILLING_DASHBOARD'),
  open_bills: plainRouteAction('ADMIN_BILLING_BILLS'),
  open_bill: idRouteAction('ADMIN_BILLING_DETAIL', ['billId', 'bill_id', 'id']),
  open_bill_students: idRouteAction('ADMIN_BILLING_STUDENTS', ['billId', 'bill_id', 'id']),
  manage_bill_items: plainRouteAction('ADMIN_BILLING_ITEMS'),

  // --- settings ------------------------------------------------------------

  open_custom_fields_settings: plainRouteAction('admin-settings', { tab: 'custom_fields' }),
  open_discounts: plainRouteAction('ADMIN_BILLING_DISCOUNTS'),
  open_refunds: plainRouteAction('ADMIN_BILLING_REFUNDS'),
  open_reports: plainRouteAction('ADMIN_BILLING_REPORTS'),
  open_transactions: plainRouteAction('ADMIN_BILLING_TRANSACTIONS'),
};

export function getAiCardActionDefinition(key: string): AiCardActionDefinition | null {
  return AI_CARD_ACTION_REGISTRY[key] ?? null;
}

/** A backend-declared action paired with its resolved frontend handler. */
export interface ResolvedAiCardAction extends AiCardAction {
  mode: AiActionMode;
  /** Produce the outcome for the host to carry out. */
  resolve: () => AiActionOutcome | null | Promise<AiActionOutcome | null>;
}

/**
 * Filter the backend's declared actions down to the ones this frontend can
 * actually perform, and bind each to its handler.
 *
 * Skipped silently: unknown keys, and known keys whose prerequisites aren't met
 * (missing id, or a route that doesn't exist in this build).
 */
export function resolveAiCardActions(
  actions: AiCardAction[] | null | undefined,
  props: Record<string, unknown>,
  router: Router,
  title?: string | null,
): ResolvedAiCardAction[] {
  if (!Array.isArray(actions) || actions.length === 0) return [];

  const resolved: ResolvedAiCardAction[] = [];
  for (const action of actions) {
    if (!action || typeof action.key !== 'string' || !action.key) continue;
    const definition = getAiCardActionDefinition(action.key);
    if (!definition) continue; // unknown key -> render nothing

    const ctx: AiCardActionContext = { props, title, router };
    if (!definition.isAvailable(ctx)) continue;

    resolved.push({
      key: action.key,
      label: typeof action.label === 'string' && action.label ? action.label : action.key,
      icon: typeof action.icon === 'string' ? action.icon : null,
      mode: definition.mode,
      resolve: () => definition.resolve(ctx),
    });
  }
  return resolved;
}
